/**
 * Cycle trial balance is unique per glcode + location (same source as getglaccounts).
 * Consolidated task locations may link any GL that exists in cycle TB (NXTGEN-7862).
 */

import { canonicalLocationKey } from './locationName';
import {
  buildLocationTypeById,
  isConsolidatedLocationId,
} from './locationMasterType';

function canonicalId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    if (value.$oid) return String(value.$oid);
    if (value._id != null && value._id !== value) return canonicalId(value._id);
    return String(value._id || '');
  }
  return String(value).trim();
}

function tbRecordMatchesLocation(record, { locationId, locationName } = {}) {
  const details = Array.isArray(record?.location_detail) ? record.location_detail : [];
  const idNeedle = canonicalId(locationId);
  const nameNeedle = canonicalLocationKey(locationName);
  return details.some((detail) => {
    if (idNeedle && canonicalId(detail?._id) === idNeedle) return true;
    if (nameNeedle && canonicalLocationKey(detail?.name) === nameNeedle) return true;
    return false;
  });
}

export function normalizeTbGlcode(glcode) {
  if (glcode == null || glcode === '') return '';
  return String(glcode).trim();
}

export function findTbRecordForGlAtLocation(tbRecords, glcode, location = {}) {
  const gl = normalizeTbGlcode(glcode);
  if (!gl) return null;
  return (
    (tbRecords || []).find(
      (row) =>
        normalizeTbGlcode(row?.glcode) === gl && tbRecordMatchesLocation(row, location)
    ) || null
  );
}

export function glExistsInTb(tbRecords, glcode) {
  const gl = normalizeTbGlcode(glcode);
  if (!gl) return false;
  return (tbRecords || []).some((row) => normalizeTbGlcode(row?.glcode) === gl);
}

function locationLookup(location = {}) {
  if (location == null || location === '') return { locationId: '', locationName: '' };
  if (typeof location !== 'object') {
    return { locationId: canonicalId(location), locationName: '' };
  }
  return {
    locationId: canonicalId(location.locationId || location.location_id || location._id || location.id),
    locationName: location.locationName || location.name || '',
  };
}

/**
 * Entity locations match TB rows at that site. Consolidated locations match any
 * TB row with the same glcode.
 * @param {object[]} tbRecords
 * @param {unknown} glcode
 * @param {unknown} location
 * @param {object[]} [locations]
 * @returns {object|null}
 */
export function resolveTbRecordForTaskLocation(
  tbRecords,
  glcode,
  location = {},
  locations = []
) {
  const lookup = locationLookup(location);
  if (isConsolidatedLocationId(lookup.locationId, buildLocationTypeById(locations))) {
    const gl = normalizeTbGlcode(glcode);
    if (!gl) return null;
    return (
      (tbRecords || []).find((row) => normalizeTbGlcode(row?.glcode) === gl) || null
    );
  }
  return findTbRecordForGlAtLocation(tbRecords, glcode, lookup);
}

export function buildValidGlCodeSet(tbRecords = []) {
  return new Set(
    (tbRecords || []).map((record) => normalizeTbGlcode(record?.glcode)).filter(Boolean)
  );
}

/**
 * Trial-balance document id. Same GL at two locations are two rows.
 * @param {object} [record]
 * @returns {string}
 */
export function tbRecordId(record) {
  if (!record) return '';
  return canonicalId(record._id);
}

/**
 * Checkbox / chip key: TB id when present, otherwise glcode (legacy).
 * @param {object} [record]
 * @returns {string}
 */
export function tbSelectionKey(record) {
  const id = tbRecordId(record);
  if (id) return id;
  return normalizeTbGlcode(record?.glcode);
}

/**
 * @param {object} [record]
 * @returns {string}
 */
export function tbLocationDisplayName(record) {
  const details = Array.isArray(record?.location_detail) ? record.location_detail : [];
  return details
    .map((detail) => (detail?.name != null ? String(detail.name).trim() : ''))
    .filter(Boolean)
    .join(', ');
}

/**
 * Restore picker selection from the task. `__tblinks` (TB ids) win so one
 * location row of a shared GL does not select every copy.
 * @param {object} [task]
 * @returns {string[]}
 */
export function selectionKeysFromTask(task) {
  const links = Array.isArray(task?.__tblinks)
    ? task.__tblinks
        .map((id) => canonicalId(id) || String(id == null ? '' : id).trim())
        .filter(Boolean)
    : [];
  if (links.length) return [...new Set(links)];
  const items = Array.isArray(task?.__tbtotals?.items) ? task.__tbtotals.items : [];
  return [...new Set(items.map((item) => tbSelectionKey(item)).filter(Boolean))];
}

/**
 * @param {object[]} [records]
 * @returns {Record<string, object>}
 */
export function indexTbRecordsById(records = []) {
  const map = {};
  (Array.isArray(records) ? records : []).forEach((item) => {
    const id = tbRecordId(item);
    if (id) map[id] = item;
  });
  return map;
}

/**
 * Selected trial-balance ids, order-preserving and de-duplicated.
 * @param {unknown[]} [selectedKeys]
 * @returns {string[]}
 */
export function selectedTbLinkIds(selectedKeys = []) {
  const seen = new Set();
  const ids = [];
  for (const raw of Array.isArray(selectedKeys) ? selectedKeys : []) {
    const id = String(raw == null ? '' : raw).trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * Selected ids with no row in the loaded metadata map.
 * @param {unknown[]} [selectedIds]
 * @param {Record<string, object>} [byId]
 * @returns {string[]}
 */
export function missingTbRecordIds(selectedIds = [], byId = {}) {
  const map = byId && typeof byId === 'object' ? byId : {};
  return selectedTbLinkIds(selectedIds).filter((id) => !map[id]);
}

/**
 * Build `__tblinks` from every selected id. Totals/items use only loaded rows.
 * @param {unknown[]} [selectedIds]
 * @param {Record<string, object>} [byId]
 * @returns {{ links: string[], items: object[], total: number }}
 */
export function buildSelectedTbTotals(selectedIds = [], byId = {}) {
  const map = byId && typeof byId === 'object' ? byId : {};
  const links = selectedTbLinkIds(selectedIds);
  const items = [];
  let total = 0;
  for (const id of links) {
    const item = map[id];
    if (!item) continue;
    items.push(item);
    if (item.closing_amount == null || item.closing_amount === '') continue;
    const amount = parseFloat(item.closing_amount);
    if (!Number.isNaN(amount)) total += amount;
  }
  return { links, items, total };
}
